import { AnimatePresence, motion } from "motion/react"

import { defaultAiChatWelcomeMessage } from "@productlane/lib/src/ai-chat-defaults"
import { starter } from "@productlane/lib/src/plans-client"
import { Switch } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { toast } from "@/components/toast"
import { UpgradeButton } from "@/components/upgrade-button"
import { docsDefaultExampleQuestions } from "@/constants"
import { analytics } from "@/lib/analytics"
import { captureError } from "@/lib/capture-error"
import { generateId } from "@/lib/id"
import { useRequiredPlans } from "@/lib/plans"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { SettingItem } from "../settings-switches/setting-item"
import { SettingsContainer } from "../settings-switches/settings-container"
import { AiCustomPromptSection } from "./ai-custom-prompt-section"

export function AiDraftsSection() {
	const workspace = useZeroWorkspace()
	const zero = useZero()

	const { passes: hasRequiredPlan } = useRequiredPlans(starter.andUp)

	const enabled = workspace?.aiChatSettings?.aiDraftsEnabled ?? false

	if (!workspace) return null

	const { aiChatSettings } = workspace

	const handleToggle = async (next: boolean) => {
		try {
			if (!aiChatSettings?.id) {
				const id = generateId()
				const welcomeMessage = defaultAiChatWelcomeMessage(workspace.name)
				await mutateOk(
					zero.mutate(
						mutators.aiChatSettings.create({
							id,
							workspaceId: workspace.id,
							welcomeMessage,
							exampleQuestions: docsDefaultExampleQuestions,
							isEnabled: false,
							aiDraftsEnabled: next,
						}),
					),
				)
			} else {
				await mutateOk(
					zero.mutate(
						mutators.aiChatSettings.update({
							id: aiChatSettings.id,
							aiDraftsEnabled: next,
						}),
					),
				)
			}
			toast.success({
				title: next ? "AI drafts enabled" : "AI drafts disabled",
			})
			analytics.track("toggle-ai-drafts", { enabled: next })
		} catch (error) {
			captureError(error, {
				domain: "ai-chat",
				action: "update ai drafts settings",
				workspaceId: workspace.id,
			})
			toast.error({ title: "Failed to update" })
		}
	}

	return (
		<div className="mt-1 flex flex-col gap-4">
			<SettingsContainer className="flex flex-col divide-none p-4">
				<SettingItem
					title="Generate drafts with AI"
					description="Drafts are grounded in thread context and your read-only sources: docs, changelog, roadmap, and whitelisted SQL. They're never sent automatically."
					action={
						hasRequiredPlan ? (
							<Switch
								name="aiDraftsEnabled"
								checked={enabled}
								onCheckedChange={handleToggle}
							/>
						) : (
							<UpgradeButton required={["starter", "pro", "scale"]} />
						)
					}
				/>
			</SettingsContainer>

			<AnimatePresence initial={false}>
				{enabled && hasRequiredPlan && (
					<motion.div
						key="ai-drafts-custom-prompt"
						initial={{ opacity: 0, height: 0 }}
						animate={{ opacity: 1, height: "auto" }}
						exit={{ opacity: 0, height: 0 }}
						transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
						className="overflow-y-clip"
					>
						<AiCustomPromptSection />
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	)
}
